import axiosInstance from "../../../axiosInstance";

const extractApiErrorMessage = (error: any, fallback: string): string => {
    if (typeof error === "string" && error.trim()) return error;
    if (typeof error?.response?.data?.error?.message === "string") return error.response.data.error.message;
    if (typeof error?.response?.data?.message === "string") return error.response.data.message;
    if (typeof error?.response?.data?.detail === "string") return error.response.data.detail;
    if (typeof error?.error?.message === "string") return error.error.message;
    if (typeof error?.error === "string") return error.error;
    if (typeof error?.message === "string") return error.message;
    return fallback;
};

// API Types based on documentation

export interface SpaceOptimizationRequest {
    request_id: string;
    engine: "FloorPlan";
    params: {
        boundary?: [number, number][];
        regions?: { x: number, y: number, width: number, height: number }[];
        rooms: { name: string, width: number, height: number, max_expansion?: number }[];
        adjacency?: [string, string][];
        non_adjacency?: [string, string][];
        adjacencies?: [string, string][];
        non_adjacencies?: [string, string][];
        fixed_rooms?: { name: string, x: number, y: number, width: number, height: number, is_fixed: true, max_expansion?: number }[];
        entrance_coords?: [[number, number], [number, number]];
        max_attempts?: number;
        allow_partial?: boolean;
    };
    ops?: string[];
}

export interface Wall {
    id: string;
    roomIds: string[];
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export interface RoomWithWalls {
    id: string;
    name: string;
    color?: string;
    walls: Wall[];
}

export interface GAOptimizationRequest {
    request_id: string;
    engine: "GA_FloorPlan";
    params: {
        plot_width: number;
        plot_height: number;
        corridor_width?: number;
        ga_config?: {
            population_size: number;
            num_generations: number;
            mutation_rate: number;
        };
        rooms: RoomWithWalls[];
        walls: Wall[];
        labels?: { x: number, y: number, text: string, roomId: string }[];
        windows?: any[];
        doors?: any[];
    };
}

export interface TaskResponse {
    task_id: string;
}

export interface TaskResult {
    status: "PENDING" | "SUCCESS" | "FAILURE" | "ok" | "error" | "pending" | "progress" | "success";
    result?: any; // The full result payload (Space or GA response data)
    data?: any; // Some APIs return data directly
    params?: any; // Pre-formatted rooms/walls/labels from the API
    error?: string;
}

// Helper for dynamic headers
const getHeaders = () => {
    return {
        "accept": "application/json",
        "content-type": "application/json",
    };
};

export const fetchSpaceOptimization = async (payload: SpaceOptimizationRequest): Promise<TaskResponse | { error: string }> => {
    try {
        // Double check endpoint: /generate/space-optimized (no slash at end?)
        const response = await axiosInstance.post(`/api/generate/space-optimized`, payload, {
            headers: getHeaders(),
        });
        return response.data;
    } catch (error) {
        console.error("Space Optimization API Error:", error);
        return { error: extractApiErrorMessage(error, "Failed to fetch space optimization") };
    }
};

export const fetchGAOptimization = async (payload: GAOptimizationRequest): Promise<TaskResponse | { error: string }> => {
    try {
        // Endpoint: /generate/ga_optimization/ (slash at end)
        const response = await axiosInstance.post(`/api/generate/ga-optimized`, payload, {
            headers: getHeaders(),
        });
        return response.data;
    } catch (error) {
        console.error("GA Optimization API Error:", error);
        return { error: extractApiErrorMessage(error, "Failed to fetch GA optimization") };
    }
};

export const fetchTaskResult = async (taskId: string): Promise<TaskResult> => {
    try {
        // Endpoint: /task/<id>/ (slash at end)
        const response = await axiosInstance.get(`/api/task/${taskId}/`, {
            headers: getHeaders(),
        });
        return response.data;
    } catch (error) {
        console.error("Task Result API Error:", error);
        return { status: "FAILURE", error: extractApiErrorMessage(error, "Failed to fetch task result") };
    }
};

// New Input Editor Document APIs

export interface EditorStateSnapshot {
    rooms: any[];
    fixedRooms: any[];
    adjacencyPairs: any[];
    nonAdjacencyPairs: any[];
    canvasLines: any[];
    zoomTransform: { x: number; y: number; k: number };
    gridSpacing: number;
    autoExpand?: boolean;
    activeSection: string | null;
    floorplans: any[];
    apiBoundary: [number, number][];
    idToNameMap: Record<string, string>;
    currentFloorplanIndex?: number;
    spaceResult?: any;
    gaResult?: any;
}

export interface NewInputDocumentPayload {
    name: string;
    document_type: "NEW_INPUT";
    editor_origin: string;
    new_input_saved_data: EditorStateSnapshot;
}

export const DEFAULT_NEW_INPUT_DOCUMENT_TITLE = "Untitled Document 1";

export const buildNewInputDocumentPayload = (
    documentTitle: string,
    editorState: EditorStateSnapshot,
): NewInputDocumentPayload => ({
    name: documentTitle.trim() || DEFAULT_NEW_INPUT_DOCUMENT_TITLE,
    document_type: "NEW_INPUT",
    editor_origin: "/newinputeditor",
    new_input_saved_data: editorState,
});

export interface DocumentSaveResponse {
    id: string;
    name: string;
    document_type?: string;
    editor_origin?: string;
    new_input_saved_data?: EditorStateSnapshot;
    created_at?: string;
    updated_at?: string;
}

export const createNewInputDocument = async (payload: NewInputDocumentPayload): Promise<DocumentSaveResponse | { error: string }> => {
    try {
        const response = await axiosInstance.post(`/api/document/`, payload, {
            headers: getHeaders(),
        });
        return response.data;
    } catch (error) {
        console.error("Create New Input Document API Error:", error);
        return { error: extractApiErrorMessage(error, "Failed to save document") };
    }
};

export const updateNewInputDocument = async (documentId: string, payload: Partial<NewInputDocumentPayload>): Promise<DocumentSaveResponse | { error: string }> => {
    try {
        // Try PUT first
        try {
            const response = await axiosInstance.put(`/api/document/${documentId}/`, payload, {
                headers: getHeaders(),
            });
            return response.data;
        } catch (putError) {
            // If PUT fails, try PATCH
            try {
                const response = await axiosInstance.patch(`/api/document/${documentId}/`, payload, {
                    headers: getHeaders(),
                });
                return response.data;
            } catch (patchError) {
                // If PATCH also fails, try POST with id field
                const payloadWithId = { ...payload, id: documentId };
                const response = await axiosInstance.post(`/api/document/`, payloadWithId, {
                    headers: getHeaders(),
                });
                return response.data;
            }
        }
    } catch (error) {
        console.error("Update New Input Document API Error:", error);
        return { error: extractApiErrorMessage(error, "Failed to update document") };
    }
};
